import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Container } from '@mui/material';
import { LessonViewer } from '../components/shared/LessonViewer';
import { getModule, getModuleContent } from '../services/lessonService';
import { useAuth } from '../hooks/useAuth';

export const ViewLesson: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [module, setModule] = useState<any>(null);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isTeacher = user?.role === 'teacher';

  useEffect(() => {
    loadLesson();
  }, [moduleId]);

  const loadLesson = async () => {
    if (!moduleId) return;

    try {
      setLoading(true);

      // Get module metadata
      const moduleData = await getModule(moduleId);
      setModule(moduleData);

      const contentMetadata = await getModuleContent(moduleId);
      const contentUnits = (contentMetadata.units || []).filter((unit: any) => unit.content);
      if (contentUnits.length === 0) {
        throw new Error('No content available for this lesson');
      }
      setUnits(contentUnits);
    } catch (err: any) {
      console.error('Error loading lesson:', err);
      setError(err.message || 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    // For students: mark as complete and navigate back
    // For teachers: just navigate back
    alert('Lesson completed!');
    handleExit();
  };

  const handleExit = () => {
    if (isTeacher) {
      navigate('/teacher/lessons');
    } else {
      navigate('/student/lessons');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !module || units.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Lesson not found or has no content'}
        </Alert>
      </Container>
    );
  }

  return (
    <LessonViewer
      moduleTitle={module.title}
      units={units}
      onComplete={handleComplete}
      onExit={handleExit}
      isTeacherPreview={isTeacher}
      moduleId={moduleId}
    />
  );
};
